import { useCallback, useEffect, useState } from 'react';
import type { StickerType, TypeUsage, UsageSummary } from '@astral/shared';
import { api, ApiClientError } from '../lib/api.js';

interface UseUsageResult {
  usage: UsageSummary | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Usage row for a given type, if loaded. */
  forType: (type: StickerType) => TypeUsage | undefined;
}

/** Fetch the current user's plan + monthly pack usage. */
export function useUsage(): UseUsageResult {
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .usage()
      .then((result) => {
        if (!cancelled) setUsage(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiClientError ? err.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const forType = useCallback(
    (type: StickerType) => usage?.types.find((t) => t.type === type),
    [usage],
  );

  return { usage, loading, error, reload, forType };
}
